/**
 * useExportComparison Hook
 *
 * Handles export logic for generating PDFs and images from fund comparison views.
 * Builds on useExportAnalysis with comparison-specific enhancements.
 */

import { useState, useCallback, useMemo, type RefObject } from "react";
import { toast } from "sonner";
import type { Fund } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

export type ExportFormat = "pdf" | "image";

export interface ComparisonExportOptions {
  /** Custom filename prefix */
  fileName?: string;
  /** Custom title for the exported document */
  title?: string;
  /** Custom subtitle for the exported document */
  subtitle?: string;
  /** Include overlap analysis in export */
  includeOverlap?: boolean;
}

export interface UseExportComparisonReturn {
  /** Export to PDF format */
  exportToPdf: (options?: ComparisonExportOptions) => Promise<void>;
  /** Export to image format (with Web Share API support) */
  exportToImage: (options?: ComparisonExportOptions) => Promise<void>;
  /** Export to specified format */
  exportTo: (format: ExportFormat, options?: ComparisonExportOptions) => Promise<void>;
  /** Whether an export is currently in progress */
  isExporting: boolean;
  /** The format currently being exported, if any */
  exportingFormat: ExportFormat | null;
  /** Whether the browser supports Web Share API for images */
  canShareImages: boolean;
  /** Generate shareable link for comparison */
  generateShareLink: () => string;
  /** Copy shareable link to clipboard */
  copyShareLink: () => Promise<void>;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_FILE_NAME = "fund-comparison";

const PDF_SETTINGS = {
  margin: 10,
  imageQuality: 0.95,
  format: "a4" as const,
};

// ============================================================================
// Web Share API Support (computed once at module load)
// ============================================================================

const CAN_SHARE = typeof navigator !== "undefined" && typeof navigator.share === "function";

/**
 * Check if the browser supports sharing files
 */
function canShareFiles(): boolean {
  if (!CAN_SHARE) return false;
  try {
    const testFile = new File(["test"], "test.png", { type: "image/png" });
    return Boolean(navigator.canShare?.({ files: [testFile] }));
  } catch {
    return false;
  }
}

const CAN_SHARE_FILES = canShareFiles();

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a timestamped filename
 */
function generateFileName(baseName: string, extension: string): string {
  const timestamp = new Date().toISOString().split("T")[0];
  const sanitizedName = baseName.replace(/[^a-zA-Z0-9-_]/g, "-").toLowerCase();
  return `${sanitizedName}-${timestamp}.${extension}`;
}

/**
 * Generate fund names summary for filename
 */
function generateFundsSummary(funds: Fund[]): string {
  if (funds.length === 0) return "comparison";
  if (funds.length === 1) return funds[0]?.amcName ?? "comparison";
  if (funds.length === 2) {
    return `${funds[0]?.amcName ?? "fund1"}-vs-${funds[1]?.amcName ?? "fund2"}`;
  }
  return `${funds.length}-funds-comparison`;
}

/**
 * Convert HTMLElement to canvas using html2canvas
 */
async function elementToCanvas(element: HTMLElement): Promise<HTMLCanvasElement> {
  const html2canvas = (await import("html2canvas")).default;

  const canvas = await html2canvas(element, {
    scale: 2, // Higher resolution
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
    scrollY: -window.scrollY,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });

  return canvas;
}

/**
 * Convert canvas to PDF and trigger download
 */
async function canvasToPdf(
  canvas: HTMLCanvasElement,
  fileName: string,
  title?: string,
  subtitle?: string,
  fundCount?: number
): Promise<void> {
  const { jsPDF } = await import("jspdf");

  const imgData = canvas.toDataURL("image/png", PDF_SETTINGS.imageQuality);
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: PDF_SETTINGS.format,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = PDF_SETTINGS.margin;
  const contentWidth = pageWidth - 2 * margin;

  let yOffset = margin;

  // Title
  if (title) {
    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    pdf.text(title, margin, yOffset + 6);
    yOffset += 12;
  }

  // Subtitle
  if (subtitle) {
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(80);
    pdf.text(subtitle, margin, yOffset + 3);
    pdf.setTextColor(0);
    yOffset += 8;
  }

  // Fund count badge
  if (fundCount && fundCount > 0) {
    pdf.setFontSize(9);
    pdf.setTextColor(100);
    pdf.text(`Comparing ${fundCount} funds`, margin, yOffset + 2);
    pdf.setTextColor(0);
    yOffset += 6;
  }

  // Generation date
  pdf.setFontSize(8);
  pdf.setTextColor(150);
  pdf.text(
    `Generated on ${new Date().toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })}`,
    margin,
    yOffset + 3
  );
  pdf.setTextColor(0);
  yOffset += 8;

  // Separator line
  pdf.setDrawColor(200);
  pdf.line(margin, yOffset, pageWidth - margin, yOffset);
  yOffset += 5;

  // Calculate image dimensions to fit the page
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  const availableHeight = pageHeight - yOffset - margin - 10; // 10mm for footer

  // Handle multi-page if content is too tall
  if (imgHeight > availableHeight) {
    // Calculate how many pages we need
    const scale = imgWidth / canvas.width;
    const pageContentHeight = availableHeight / scale;
    let srcY = 0;
    let pageNum = 1;

    while (srcY < canvas.height) {
      if (pageNum > 1) {
        pdf.addPage();
        yOffset = margin;
      }

      const remainingHeight = canvas.height - srcY;
      const sliceHeight = Math.min(pageContentHeight, remainingHeight);
      const destHeight = sliceHeight * scale;

      // Create a temporary canvas for this slice
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeight;
      const ctx = sliceCanvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(
          canvas,
          0, srcY, canvas.width, sliceHeight,
          0, 0, canvas.width, sliceHeight
        );
        const sliceData = sliceCanvas.toDataURL("image/png", PDF_SETTINGS.imageQuality);
        pdf.addImage(sliceData, "PNG", margin, yOffset, imgWidth, destHeight);
      }

      srcY += sliceHeight;
      pageNum++;
    }
  } else {
    pdf.addImage(imgData, "PNG", margin, yOffset, imgWidth, imgHeight);
  }

  // Add footer on last page
  const footerY = pageHeight - 5;
  pdf.setFontSize(8);
  pdf.setTextColor(150);
  pdf.text("Generated by Curie Compass", margin, footerY);
  pdf.text("curie.money", pageWidth - margin - 20, footerY);

  pdf.save(fileName);
}

/**
 * Convert canvas to image blob
 */
async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create image blob"));
        }
      },
      "image/png",
      PDF_SETTINGS.imageQuality
    );
  });
}

/**
 * Share image using Web Share API
 */
async function shareImage(blob: Blob, fileName: string, title: string): Promise<boolean> {
  const file = new File([blob], fileName, { type: "image/png" });

  if (CAN_SHARE_FILES) {
    await navigator.share({
      files: [file],
      title,
      text: "Fund comparison from Curie Compass",
    });
    return true;
  }
  return false;
}

/**
 * Download blob as file
 */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useExportComparison
 *
 * Hook for exporting fund comparison views as PDF or image.
 *
 * @param targetRef - Ref to the HTML element to export
 * @param funds - Array of funds being compared
 * @returns Export functions and state
 *
 * @example
 * ```tsx
 * function CompareView() {
 *   const contentRef = useRef<HTMLDivElement>(null);
 *   const { exportToPdf, exportToImage, isExporting, copyShareLink } = useExportComparison(contentRef, funds);
 *
 *   return (
 *     <div>
 *       <button onClick={() => exportToPdf()} disabled={isExporting}>
 *         Export PDF
 *       </button>
 *       <button onClick={copyShareLink}>
 *         Copy Link
 *       </button>
 *       <div ref={contentRef}>
 *         {/* Comparison content *\/}
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function useExportComparison(
  targetRef: RefObject<HTMLElement | null>,
  funds: Fund[]
): UseExportComparisonReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);

  /**
   * Generate shareable link for the current comparison
   */
  const generateShareLink = useCallback((): string => {
    if (typeof window === "undefined") return "";

    const url = new URL(window.location.origin + "/compare");
    funds.forEach((fund) => {
      url.searchParams.append("funds[]", fund.isin);
    });

    return url.toString();
  }, [funds]);

  /**
   * Copy shareable link to clipboard
   */
  const copyShareLink = useCallback(async () => {
    const link = generateShareLink();

    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied!", {
        description: "Share this link to let others see this comparison",
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Failed to copy link", {
        description: "Please try again",
        duration: 3000,
      });
    }
  }, [generateShareLink]);

  const exportTo = useCallback(
    async (format: ExportFormat, options?: ComparisonExportOptions) => {
      if (isExporting || !targetRef.current) return;

      const fundsSummary = generateFundsSummary(funds);
      const fileName = options?.fileName ?? `${DEFAULT_FILE_NAME}-${fundsSummary}`;
      const defaultTitle = `Fund Comparison: ${funds.length} Funds`;
      const defaultSubtitle = funds.map((f) => f.amcName).join(" vs ");

      setIsExporting(true);
      setExportingFormat(format);

      const toastId = toast.loading(
        format === "pdf" ? "Generating comparison PDF..." : "Generating comparison image...",
        { description: "Please wait while we prepare your export" }
      );

      try {
        const canvas = await elementToCanvas(targetRef.current);

        if (format === "pdf") {
          const pdfFileName = generateFileName(fileName, "pdf");
          await canvasToPdf(
            canvas,
            pdfFileName,
            options?.title ?? defaultTitle,
            options?.subtitle ?? defaultSubtitle,
            funds.length
          );

          toast.success("PDF downloaded successfully!", {
            id: toastId,
            description: pdfFileName,
            duration: 4000,
          });
        } else {
          const imageFileName = generateFileName(fileName, "png");
          const blob = await canvasToBlob(canvas);

          const shared = await shareImage(
            blob,
            imageFileName,
            options?.title ?? defaultTitle
          );

          if (shared) {
            toast.success("Ready to share!", {
              id: toastId,
              description: "Select where you want to share",
              duration: 3000,
            });
          } else {
            downloadBlob(blob, imageFileName);
            toast.success("Image downloaded successfully!", {
              id: toastId,
              description: imageFileName,
              duration: 4000,
            });
          }
        }
      } catch (error) {
        console.error("Export error:", error);
        const err = error instanceof Error ? error : new Error("Failed to export comparison");

        toast.error("Export failed", {
          id: toastId,
          description: err.message || "Please try again",
          duration: 4000,
        });
      } finally {
        setIsExporting(false);
        setExportingFormat(null);
      }
    },
    [isExporting, targetRef, funds]
  );

  const exportToPdf = useCallback(
    (options?: ComparisonExportOptions) => exportTo("pdf", options),
    [exportTo]
  );

  const exportToImage = useCallback(
    (options?: ComparisonExportOptions) => exportTo("image", options),
    [exportTo]
  );

  return useMemo(
    () => ({
      exportToPdf,
      exportToImage,
      exportTo,
      isExporting,
      exportingFormat,
      canShareImages: CAN_SHARE_FILES,
      generateShareLink,
      copyShareLink,
    }),
    [exportToPdf, exportToImage, exportTo, isExporting, exportingFormat, generateShareLink, copyShareLink]
  );
}
